import React, { useState } from 'react';
import { Send } from 'lucide-react';
import { Button } from '../ui/button';
import { messageAuthorLabel, formatHistoryWhen } from './workItemHistory';
import { isChaseable } from './workItemQueueFilters';
import WorkItemFileDrop from './WorkItemFileDrop';
import WorkItemAttachmentView from './WorkItemAttachmentView';
import { pendingBlob, pendingMeta } from './workItemFileItems';

/**
 * Messages + chase drafts for a work item.
 */
const WorkItemConversationTab = ({
  detail,
  postWorkItemMessage,
  requestAttachmentUpload,
  downloadWorkItemAttachment,
  removeWorkItemAttachment,
  renameWorkItemAttachment,
  createChaseDraft,
  approveChaseDraft,
  showAlert,
  onItemUpdated,
  resolveName,
  profileSub,
}) => {
  const [message, setMessage] = useState('');
  const [internalMsg, setInternalMsg] = useState(false);
  const [chaseBody, setChaseBody] = useState('');
  const [pendingFiles, setPendingFiles] = useState([]);

  const messages = (detail.timeline || []).filter(
    (e) => e.kind === 'message' || e.kind === 'nudge_sent' || e.kind === 'file_uploaded',
  );
  const pendingDrafts = (detail.draftMessages || []).filter((d) => d.status === 'pending_approval');
  const chaseable = isChaseable(detail);
  const chaseBlockedReason = (() => {
    if (chaseable) return null;
    if (detail.visibilityState === 'draft') return 'Publish the work item before chasing the client.';
    if (!detail.shared) return 'Share with the client first — chase is a client reminder.';
    if (detail.taskHolder !== 'responding') {
      return 'Chase is only for work waiting on the client (status Waiting on client).';
    }
    return 'This work item cannot be chased right now.';
  })();

  const renderAttachments = (entry) => {
    const atts = entry.attachments || [];
    if (!atts.length) return null;
    return (
      <ul className='mt-1 space-y-1'>
        {atts.map((att) => (
          <li key={att.id}>
            <WorkItemAttachmentView
              workItemId={detail.id}
              attachment={att}
              downloadWorkItemAttachment={downloadWorkItemAttachment}
              removeWorkItemAttachment={removeWorkItemAttachment}
              renameWorkItemAttachment={renameWorkItemAttachment}
              showAlert={showAlert}
              onItemUpdated={onItemUpdated}
              compact
            />
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div className='space-y-4'>
      <div className='max-h-64 overflow-y-auto space-y-2 text-sm'>
        {messages.length === 0 ? (
          <p className='text-sm text-muted-foreground'>
            No messages yet. Post the ask or keep internal notes here.
          </p>
        ) : (
          messages.map((e) => {
            const author = messageAuthorLabel(e, resolveName, profileSub);
            const kindLabel =
              e.kind === 'file_uploaded' ? 'file' : e.kind === 'nudge_sent' ? 'chase' : null;
            return (
              <div key={e.id} className='rounded border border-border p-2'>
                <div className='flex justify-between gap-2 text-xs text-muted-foreground'>
                  <span className='font-medium text-foreground'>
                    {author}
                    {e.internal ? (
                      <span className='ml-1 font-normal text-muted-foreground'>· internal</span>
                    ) : null}
                    {kindLabel ? (
                      <span className='ml-1 font-normal text-muted-foreground'>· {kindLabel}</span>
                    ) : null}
                  </span>
                  <span className='shrink-0'>{formatHistoryWhen(e.at)}</span>
                </div>
                <p className='mt-1 text-foreground'>{e.body || (e.attachments ? 'Attachment' : '')}</p>
                {renderAttachments(e)}
              </div>
            );
          })
        )}
      </div>

      <textarea
        className='w-full min-h-[64px] rounded-md border border-border bg-input px-3 py-2 text-sm'
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder='Write a message…'
      />
      <WorkItemFileDrop files={pendingFiles} onChange={setPendingFiles} />
      <div className='flex flex-wrap items-center gap-2'>
        <label className='text-xs flex items-center gap-1'>
          <input type='checkbox' checked={internalMsg} onChange={(e) => setInternalMsg(e.target.checked)} />
          Internal
        </label>
        <Button
          size='sm'
          onClick={async () => {
            if (!message.trim() && !pendingFiles.length) return;
            let attachments = null;
            if (pendingFiles.length) {
              const meta = await requestAttachmentUpload(detail.id, {
                kind: 'message',
                files: pendingFiles.map(pendingMeta),
              });
              if (!meta?.ok) {
                showAlert(true, meta?.message || 'Upload prepare failed.', 'error', 5000);
                return;
              }
              const rows = meta.attachments || [];
              try {
                await Promise.all(
                  rows.map((row, i) =>
                    fetch(row.uploadUrl, {
                      method: 'PUT',
                      headers: { 'Content-Type': pendingMeta(pendingFiles[i]).contentType },
                      body: pendingBlob(pendingFiles[i]),
                    }),
                  ),
                );
              } catch (err) {
                showAlert(true, 'File upload failed.', 'error', 5000);
                return;
              }
              attachments = rows.map(({ uploadUrl, ...rest }) => rest);
            }
            const result = await postWorkItemMessage(
              detail.id,
              message.trim() || pendingMeta(pendingFiles[0]).name,
              internalMsg,
              attachments,
            );
            if (result?.ok) {
              setMessage('');
              setPendingFiles([]);
              onItemUpdated(result.item);
            } else {
              showAlert(true, result?.message || 'Send failed.', 'error', 5000);
            }
          }}>
          <Send size={14} strokeWidth={1.5} />
          Post
        </Button>
      </div>

      <div className='space-y-2 border-t border-border pt-3'>
        <p className='text-xs font-medium text-muted-foreground'>Chase (client reminder)</p>
        <p className='text-xs text-muted-foreground'>
          A chase is an approval-first reminder draft sent to the client when the ball is with them.
        </p>
        {chaseable ? (
          <>
            <textarea
              className='w-full min-h-[56px] rounded-md border border-border bg-input px-3 py-2 text-sm'
              value={chaseBody}
              onChange={(e) => setChaseBody(e.target.value)}
              placeholder='Draft chase message to client…'
            />
            <Button
              size='sm'
              variant='outline'
              onClick={async () => {
                const body =
                  chaseBody.trim() ||
                  `Friendly reminder: please complete "${detail.title}"` +
                    (detail.dueDate ? ` (due ${detail.dueDate})` : '');
                const result = await createChaseDraft(detail.id, body);
                if (result?.ok) {
                  setChaseBody('');
                  onItemUpdated(result.item);
                  showAlert(true, 'Chase draft ready for approval.', 'success', 4000);
                } else {
                  showAlert(true, result?.message || 'Could not create draft.', 'error', 5000);
                }
              }}>
              Draft chase for approval
            </Button>
          </>
        ) : (
          <p className='text-xs text-muted-foreground italic'>{chaseBlockedReason}</p>
        )}
        {pendingDrafts.map((d) => (
          <div key={d.id} className='rounded border border-border p-2 text-sm space-y-2'>
            <p>{d.body}</p>
            <Button
              size='sm'
              onClick={async () => {
                const result = await approveChaseDraft(detail.id, d.id);
                if (result?.ok) {
                  onItemUpdated(result.item);
                  showAlert(true, 'Chase posted (recorded).', 'success', 4000);
                } else {
                  showAlert(true, result?.message || 'Approve failed.', 'error', 5000);
                }
              }}>
              Approve &amp; post
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default WorkItemConversationTab;
